import React from "react";
import {Dialog, DialogContent, DialogTitle, IconButton, InputAdornment, TextField} from "@mui/material";
import {CloseOutlined, SearchOutlined} from "@mui/icons-material";
import type {ClContract} from "../contractLinkageData";
import {CL_CURRENCY} from "../contractLinkageData";
import type {ClReviewTx} from "../contractLinkageUtils";
import {formatCompactCurrency, formatNumber, formatPeriod, withinContractPeriod} from "../contractLinkageUtils";

type Props = {
    open: boolean;
    contracts: ClContract[];
    /** The transactions being (re)assigned — used to rank contracts and show period fit. */
    txs: ClReviewTx[];
    onPick: (contractId: string) => void;
    onClose: () => void;
};

export const ContractPicker: React.FC<Props> = ({open, contracts, txs, onPick, onClose}) => {
    const [search, setSearch] = React.useState('');

    // Reset the search whenever the dialog opens for a new assignment.
    React.useEffect(() => {
        if (open) setSearch('');
    }, [open]);

    const supplierIds = React.useMemo(() => new Set(txs.map(t => t.supplierId)), [txs]);

    const {supplierContracts, otherContracts} = React.useMemo(() => {
        const q = search.trim().toLowerCase();
        const matches = (c: ClContract) =>
            !q || c.name.toLowerCase().includes(q) || c.supplier.toLowerCase().includes(q) || c.type.toLowerCase().includes(q);
        const filtered = contracts.filter(matches);
        return {
            supplierContracts: filtered.filter(c => supplierIds.has(c.supplierId)),
            otherContracts: filtered.filter(c => !supplierIds.has(c.supplierId)),
        };
    }, [search, supplierIds, contracts]);

    const periodFit = (c: ClContract) => txs.filter(t => withinContractPeriod(t.date, c)).length;

    const renderItem = (c: ClContract) => {
        const fit = periodFit(c);
        return (
            <button key={c.id} type="button" className="cl-picker-item" onClick={() => onPick(c.id)}>
                <span className="cl-picker-item-main">
                    <span className="cl-picker-item-name">{c.name}</span>
                    <span className="cl-picker-item-meta">
                        {c.supplier} · {c.type} · {formatPeriod(c)} · {formatCompactCurrency(c.contractedValue, CL_CURRENCY)}
                    </span>
                </span>
                <span className="cl-picker-item-side">
                    <span className={`cl-contract-status is-${c.status}`}>{c.status}</span>
                    {txs.length > 0 && (
                        <span className={`cl-picker-fit${fit === txs.length ? ' is-full' : fit === 0 ? ' is-none' : ''}`}>
                            {fit === txs.length
                                ? (txs.length === 1 ? 'In period' : 'All in period')
                                : fit === 0
                                    ? 'Outside period'
                                    : `${formatNumber(fit)}/${formatNumber(txs.length)} in period`}
                        </span>
                    )}
                </span>
            </button>
        );
    };

    return (
        // The app header is pinned at theme.zIndex.drawer + 1, so lift above it
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
                sx={theme => ({zIndex: theme.zIndex.drawer + 3})}>
            <DialogTitle className="cl-picker-title">
                Link {txs.length === 1 ? 'transaction' : `${formatNumber(txs.length)} transactions`} to a contract
                <IconButton size="small" onClick={onClose}><CloseOutlined/></IconButton>
            </DialogTitle>
            <DialogContent className="cl-picker-content">
                <TextField
                    autoFocus
                    fullWidth
                    size="small"
                    placeholder="Search contracts by name, supplier or type…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start"><SearchOutlined fontSize="small"/></InputAdornment>
                            ),
                        },
                    }}
                />
                <div className="cl-picker-list">
                    {supplierContracts.length > 0 && (
                        <>
                            <div className="cl-picker-group">Contracts for the selected supplier{supplierIds.size > 1 ? 's' : ''}</div>
                            {supplierContracts.map(renderItem)}
                        </>
                    )}
                    {otherContracts.length > 0 && (
                        <>
                            <div className="cl-picker-group">{supplierContracts.length > 0 ? 'Other contracts' : 'All contracts'}</div>
                            {otherContracts.map(renderItem)}
                        </>
                    )}
                    {supplierContracts.length === 0 && otherContracts.length === 0 && (
                        <div className="cl-picker-empty">No contracts match “{search}”.</div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};
